import { ICommandDispathcer, ICommand, ICommandResult } from './types/commands';
import axios, { AxiosInstance } from 'axios';
import PQueue from 'p-queue';
import { handleError } from './errorHandler';
import { bugsnagNotify } from '../utils/bugsnag';
import { redactPublishedAccess } from '../utils/publishedAccessRedaction';
import { DiagnosticRequestConfig, RequestDiagnostics, requestDiagnostics } from './requestDiagnostics';

class CommandDispatcher implements ICommandDispathcer {
    private client: AxiosInstance;
    private queue: PQueue;

    public constructor(baseUrl: string, csrfTokenHeaderKey: string, currentCsrfToken: string | null = null) {
        this.queue = new PQueue({ concurrency: 1 });

        this.client = axios.create({
            baseURL: baseUrl,
            //timeout: 120000,
        });
        let requestStartedAt: number;
        this.client.interceptors.request.use((x: DiagnosticRequestConfig) => {
            if (x.requestDiagnostics) x.diagnosticStartedAt = new Date().getTime();
            else requestStartedAt = new Date().getTime();
            return x;
        });
        this.client.interceptors.response.use((x) => {
            const config = x.config as DiagnosticRequestConfig;
            const diagnostics = requestDiagnostics(config.requestDiagnostics);
            const responseTime = new Date().getTime() - (diagnostics ? config.diagnosticStartedAt : requestStartedAt);
            if (responseTime > 30000 && diagnostics) {
                bugsnagNotify({ name: 'Long response time', message: `Response time: ${responseTime}ms, operation: ${diagnostics.operation}, stage: ${diagnostics.stage}` });
            } else if (responseTime > 30000) {
                const requestData = JSON.stringify(redactPublishedAccess(x.config));
                bugsnagNotify({ name: 'Long response time', message: `Response time: ${responseTime}ms, request: ${requestData}` });
            }
            return x;
        });

        if (currentCsrfToken != null) {
            this.client.defaults.headers.post[csrfTokenHeaderKey] = currentCsrfToken;
        }
    }

    public abortAllCommand(): void {
        this.queue.clear();
    }

    public dispatchCommand = async <TResult extends ICommandResult>(command: ICommand<TResult>, isCurrent?: () => boolean, diagnostics?: RequestDiagnostics): Promise<TResult> => {
        const diagnosticContext = requestDiagnostics(diagnostics);
        try {
            const res = await this.queue.add(() =>
                this.client.request({
                    method: 'post',
                    url: `/${command.CommandType}`,
                    data: command,
                    requestDiagnostics: diagnosticContext,
                    validateStatus: (status: number) => {
                        return status == 200; //|| status == 403;
                    },
                } as DiagnosticRequestConfig)
            );

            if (res.status == 200) {
                return res.data;
            } else {
                console.error('[CQRS] error on api call!', res);
            }
        } catch (error) {
            if (!isCurrent || isCurrent()) {
                if (diagnosticContext) handleError(error, diagnosticContext);
                else handleError(error);
            }
            throw error;
        }
    };
}

const commandDispatcher = new CommandDispatcher(
    initAppData.appUrls.appUrl + '/api/c',
    initAppData.csrf.csrfTokenHeaderKey,
    initAppData.csrf.currentCsrfToken
);

export { commandDispatcher };
